import appLogo from "@/assets/images/app-logo.png";
import { PublicHeader } from "@/components/shared/PublicHeader";
import { useState } from "react";

export function PrivacyPolicy() {
  const [lang, setLang] = useState<"en" | "cn">("en");
  const isCn = lang === "cn";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PublicHeader activePage="privacypolicy" />
      <div className="flex-1 max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-8 py-10">
          <div className="flex justify-center mb-8">
            <img src={appLogo} alt="Pocket Teacher Logo" className="h-24 rounded-md w-auto" />
          </div>

          <div className="flex justify-center gap-3 mb-8">
            <button
              onClick={() => setLang("en")}
              type="button"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !isCn ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              English
            </button>
            <button
              onClick={() => setLang("cn")}
              type="button"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isCn ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              中文
            </button>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            {isCn ? "隱私暨個資保護聲明" : "Privacy Policy"}
          </h1>
          <p className="text-sm text-gray-500 mb-8 text-center">
            {isCn ? "最後更新：2026年4月30日" : "Last Updated: April 30, 2026"}
          </p>

          {isCn ? <ChineseContent /> : <EnglishContent />}
        </div>
      </div>
    </div>
  );
}

function EnglishContent() {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Introduction</h2>
        <p className="text-gray-700">
          Melody Publishing Co., Ltd. (hereinafter referred to as “Melody” or “we”) operates the
          Pocket Teacher application. This policy describes what information we collect when you use
          the app, how we use it, and your rights regarding that information. By using Pocket
          Teacher, you agree to the practices described in this policy.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
        <p className="text-gray-700 mb-3">
          Pocket Teacher is a sign-in-based application. We collect only the information necessary
          to authenticate you and operate the service:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>
            <strong>Account credentials:</strong> Your username and password, used solely to verify
            your identity when signing in. Passwords are never stored in plain text.
          </li>
          <li>
            <strong>Login records:</strong> IP address and timestamp each time you sign in, used for
            account security and unauthorized-access detection.
          </li>
          <li>
            <strong>Device and app information:</strong> Device type, operating system version, and
            app version, used to ensure compatibility and diagnose technical issues.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>To authenticate you and maintain your session within the app.</li>
          <li>To detect unauthorized access and protect account security.</li>
          <li>
            To diagnose technical issues and ensure the app functions correctly on your device.
          </li>
        </ul>
        <p className="text-gray-700 mt-3">
          We do not use your information for advertising or marketing, and we do not sell your data
          to any third party.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          3. How We Protect Your Information
        </h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>All data is transmitted over HTTPS/TLS-encrypted connections.</li>
          <li>Passwords are stored using industry-standard one-way hashing.</li>
          <li>
            We retain login records only as long as necessary for security purposes, in accordance
            with applicable laws.
          </li>
          <li>
            We do not share your information with third parties except as required by law or court
            order.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Your Rights</h2>
        <p className="text-gray-700 mb-3">
          You have the right to request access to, correction of, or deletion of your personal data
          held by us. To exercise these rights, please contact us using the information below.
        </p>
        <p className="text-gray-700">
          We will respond to your request within a reasonable timeframe. We may require identity
          verification before processing certain requests.
        </p>
      </section>

      <section id="data-deletion" className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Requesting Data Deletion</h2>
        <p className="text-gray-700 mb-3">
          To request deletion of your personal data, follow these steps:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4 mb-4">
          <li>
            Send an email to <strong>services@melody.com.tw</strong> with the subject line{" "}
            <strong>"Data Deletion Request"</strong>.
          </li>
          <li>Include your username in the email body so we can locate your account.</li>
          <li>
            We will confirm receipt within 3 business days and complete the deletion within 30 days.
          </li>
        </ol>
        <p className="text-gray-700 mb-3">
          <strong>What gets deleted:</strong> Your account credentials (username and hashed
          password) and associated login records.
        </p>
        <p className="text-gray-700">
          <strong>What may be retained:</strong> Anonymised security logs may be retained for up to
          90 days after your deletion request to comply with applicable laws and to prevent fraud.
          These logs cannot be used to identify you.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Changes to This Policy</h2>
        <p className="text-gray-700">
          We may update this policy as the app's features evolve. We will notify users of material
          changes via in-app notice or email. Continued use of the app after changes take effect
          constitutes acceptance of the updated policy.
        </p>
      </section>

      <section id="contact" className="pt-6 border-t border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
        <p className="text-gray-700 mb-1">
          If you have any questions about this policy, please contact us:
        </p>
        <p className="text-gray-700">
          Phone: 07-3227493
          <br />
          Email: services@melody.com.tw
        </p>
        <p className="text-sm text-gray-500 mt-6">Copyright © Melody Publishing 2026.</p>
      </section>
    </>
  );
}

function ChineseContent() {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">引言</h2>
        <p className="text-gray-700">
          美樂蒂文教科技興業股份有限公司（以下稱「美樂蒂」或「我們」）營運 Pocket Teacher
          應用程式。本聲明說明您使用本應用時我們蒐集哪些資訊、如何使用，以及您對該資訊所擁有的權利。使用
          Pocket Teacher 即表示您同意本聲明所述之做法。
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">一、我們蒐集的資訊</h2>
        <p className="text-gray-700 mb-3">
          Pocket Teacher 為登入制應用程式，我們僅蒐集驗證身份與提供服務所必要的資訊：
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>
            <strong>帳號憑證：</strong>
            您的使用者名稱與密碼，僅用於登入時驗證身份。密碼絕不以明文方式儲存。
          </li>
          <li>
            <strong>登入紀錄：</strong>每次登入時的 IP
            位址與時間戳記，用於帳號安全與偵測未授權存取。
          </li>
          <li>
            <strong>裝置與應用程式資訊：</strong>
            裝置型號、作業系統版本及應用程式版本，用於確保相容性與排除技術問題。
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">二、我們如何使用您的資訊</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>驗證您的身份並維持您在應用程式內的工作階段。</li>
          <li>偵測未授權存取，保護帳號安全。</li>
          <li>排除技術問題，確保應用程式在您的裝置上正常運作。</li>
        </ul>
        <p className="text-gray-700 mt-3">
          我們不會將您的資訊用於廣告或行銷目的，也不會將您的資料出售給任何第三方。
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">三、我們如何保護您的資訊</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
          <li>所有資料均透過 HTTPS/TLS 加密連線傳輸。</li>
          <li>密碼以業界標準的單向雜湊方式儲存。</li>
          <li>登入紀錄僅在安全目的所需期間內保留，並符合相關法規規定。</li>
          <li>除法律規定或法院命令外，我們不會將您的資訊提供給第三方。</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">四、您的權利</h2>
        <p className="text-gray-700 mb-3">
          您有權請求查閱、更正或刪除我們持有的您的個人資料。如需行使上述權利，請透過下方聯絡資訊與我們聯繫。
        </p>
        <p className="text-gray-700">
          我們將在合理時間內回應您的請求。處理特定請求前，我們可能需要進行身份驗證。
        </p>
      </section>

      <section id="data-deletion" className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">五、申請刪除資料</h2>
        <p className="text-gray-700 mb-3">如需申請刪除您的個人資料，請依照以下步驟操作：</p>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4 mb-4">
          <li>
            發送電子郵件至 <strong>services@melody.com.tw</strong>，主旨請填寫{" "}
            <strong>「資料刪除申請」</strong>。
          </li>
          <li>於信件內容中提供您的使用者名稱，以便我們找到您的帳號。</li>
          <li>我們將於 3 個工作天內確認收件，並於 30 天內完成刪除作業。</li>
        </ol>
        <p className="text-gray-700 mb-3">
          <strong>將刪除的資料：</strong>您的帳號憑證（使用者名稱與雜湊密碼）及相關登入紀錄。
        </p>
        <p className="text-gray-700">
          <strong>可能保留的資料：</strong>
          為遵守相關法規及防止詐欺，匿名化的安全日誌可能在您提出申請後保留最多 90
          天。這些日誌無法用於識別您的個人身份。
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">六、本聲明的變更</h2>
        <p className="text-gray-700">
          隨著應用程式功能的演進，本聲明可能會不定期更新。重大變更將透過應用程式內通知或電子郵件告知使用者。變更生效後繼續使用本應用，即表示您同意更新後的聲明。
        </p>
      </section>

      <section id="contact" className="pt-6 border-t border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">聯絡我們</h2>
        <p className="text-gray-700 mb-1">如對本聲明有任何疑問，歡迎與我們聯繫：</p>
        <p className="text-gray-700">
          電話：07-3227493
          <br />
          電子郵件：services@melody.com.tw
        </p>
        <p className="text-sm text-gray-500 mt-6">Copyright © Melody Publishing 2026.</p>
      </section>
    </>
  );
}
